import { landmineNames } from '@mm/constants/landmine-names';
import { useTemplatesCRUDActions } from '@mm/state/actions/TemplatesCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import circlePlus from 'assets/circle-plus.svg';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useInfiniteScroll } from '../../../../../common/hooks/use-infinite-scroll';
import { AreYouSureToRemoveTemplate } from '../../components/AreYouSureToRemoveTemplate/AreYouSureToRemoveTemplate';
import { CopyTemplateCode } from '../../components/CopyTemplateCode/CopyTemplateCode';
import CreateBotOrExtendByTemplate from '../../components/CreateBotOrExtendByTemplate/CreateBotOrExtendByTemplate';
import { CreateTemplateFromBot } from '../../components/CreateTemplateFromBot/CreateTemplateFromBot';
import { TemplateBlocks } from '../../components/TemplateBlocks/TemplateBlocks';
import { useTemplateFetchHook } from '../../hooks/use-template-fetch-hook';

export const TemplateMyPanel = () => {
  const { templates, fetchMore, moreToFetch, removeTemplate, addTemplate } =
    useTemplateFetchHook(false);
  useInfiniteScroll({
    fetchMore,
    fetchDisabled: !moreToFetch,
    useChatbotEditorPageContentContainer: true,
    scrollEndOffset: 500,
  });

  const { delete: deleteTemplate } = useTemplatesCRUDActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const [tempalteId, setTempalteId] = useState<number | undefined>(undefined);

  const [searchToken, setSearchToken] = useState<string | undefined>(undefined);

  const [isRemoveTempalte, setIsRemoveTemplate] = useState(false);
  const [isCreateOrExtendBot, setIsCreateOrExtendBot] = useState(false);
  const [isCreateTemplate, setIsCreateTemplate] = useState(false);
  const [isCopyTemplateCode, setIsCopyTemplateCode] = useState(false);
  const canCreateTemplate = useSelector(
    combinedFeatureFlagsSelector,
  ).templatesCreate;

  const closeModal = () => {
    setTempalteId(undefined);
    setSearchToken(undefined);
    setIsRemoveTemplate(false);
    setIsCreateOrExtendBot(false);
    setIsCreateTemplate(false);
    setIsCopyTemplateCode(false);
  };

  const openCreateOrExtendBotDialog = (
    templateId: number,
    searchToken: string,
  ) => {
    setSearchToken(searchToken);
    setIsCreateOrExtendBot(true);
  };

  const openAreYouSureDialog = (templateId: number) => {
    setTempalteId(templateId);
    setIsRemoveTemplate(true);
  };

  const openCreateTemplate = (
    tempalteId: number | undefined,
    isEdit?: boolean | string,
  ) => {
    if (tempalteId && Number.isFinite(tempalteId)) {
      setTempalteId(tempalteId);
    }
    if (canCreateTemplate || isEdit) {
      setIsCreateTemplate(true);
    } else {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['TEMPLATES__MY-TEMPLATES__OPEN-CREATE'],
        feature: 'templatesCreate',
      });
    }
  };

  const openCopyTemplateCode = (templateId: number, searchToken: string) => {
    setSearchToken(searchToken);
    setIsCopyTemplateCode(true);
  };

  const onRemoveTempalte = (templateId: number) => {
    deleteTemplate(templateId);
    removeTemplate(templateId);
  };

  return (
    <div>
      <p className="templates-library__info">
        Create templates to share with other Facebook Pages you work with, or to
        share outside your business with partners and colleagues.
      </p>
      <TemplateBlocks>
        <TemplateBlocks.Item
          title="Create new"
          isNew
          onClick={openCreateTemplate}
          icon={<img src={circlePlus} />}
        />
        {templates.map(model => (
          <TemplateBlocks.Item
            onClick={openCreateOrExtendBotDialog}
            onEdit={() => openCreateTemplate(model.id, true)}
            onDelete={openAreYouSureDialog}
            onGetCode={openCopyTemplateCode}
            key={model.id}
            searchToken={model.searchToken}
            title={model.name}
            subTitle="By Me"
            details={model.description}
            img={model.asset && model.asset.urls.medium}
            id={model.id}
          />
        ))}
      </TemplateBlocks>

      {isCreateOrExtendBot && searchToken !== undefined && (
        <CreateBotOrExtendByTemplate
          onClose={closeModal}
          searchToken={searchToken}
        />
      )}
      {isRemoveTempalte && tempalteId !== undefined && (
        <AreYouSureToRemoveTemplate
          onClose={closeModal}
          templateId={tempalteId}
          onRemove={onRemoveTempalte}
        />
      )}
      {isCreateTemplate && (
        <CreateTemplateFromBot
          onClose={closeModal}
          templateId={tempalteId}
          onCreatedTemplate={addTemplate}
        />
      )}
      {isCopyTemplateCode && (
        <CopyTemplateCode onClose={closeModal} searchToken={searchToken} />
      )}
    </div>
  );
};
